/**
 * @module components/trace-live-plot
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * Chart component of stream response
 */

import { useMemo, useState, useEffect } from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Label,
} from 'recharts';

import moment from 'moment';

import { getWaveformID } from '../../lib/stream';

import { WaveformTrace } from '../../typings/waveform-plot';

type ChartDataPoint = {
  time: number,
  value: number,
}

type ChartData = ChartDataPoint[];


/**
 * Convert trace to data object for recharts
 */
const traceToData = (
  trace: WaveformTrace,
): ChartData => {
  console.log(trace)
  return trace.data.map((value, idx) => {
    return {
      time: trace.header.starttime.clone().add(trace.header.delta*idx, 's').valueOf(),
      value
    };
  })
};


export type TraceProps = {
  /**
   * Precision of the Y axis units
   */
   precision?: number,
   /**
    * Datetime format display
    */
   datetimefmt?: string,
   /**
    * Data display period (window) in seconds
    */
   dataPeriod?: number,
   /**
    * Delay in seconds the add to live display
    */
   dataDelay?: number,
   /**
    * Refresh rate of the plot in seconds
    */
   updateInterval?: number,
   /**
    * x title
    */
   xtitle?: string,
}


type Props = {
  /**
   * Trace index to plot from the context
   */
  trace: WaveformTrace,
} & TraceProps


export const TracePlot = ({
  trace,
  precision = 1,
  datetimefmt = 'HH:mm:ss z',
  dataPeriod = 60,
  dataDelay = 0,
  updateInterval = undefined,
  xtitle = ','
}: Props) => {
  const memoizedData = useMemo<ChartData>(() => traceToData(trace), [trace]);
  const memoizedID = useMemo<string>(() => getWaveformID(trace), [trace]);

  const [ endtime, setEndtime ] = useState(moment().subtract(dataDelay, 's'))

  console.log(`Updating plot for ${trace.header.station}`)

  useEffect(() => {
    let myInterval: NodeJS.Timer | undefined = undefined;
    if(updateInterval)
      myInterval = setInterval(() => setEndtime(moment().subtract(dataDelay, 's')), updateInterval*1000)
    // clear the interval if its already set and fetch interval undefined
    if(!updateInterval && myInterval)
      clearInterval(myInterval)
    return () => {
      if(myInterval) clearInterval(myInterval)
    }
  }, [updateInterval, setEndtime, dataDelay])

  return (
    <ResponsiveContainer width="100%" aspect={20.0/3.0}>
      <LineChart
        data={memoizedData}
        margin={{top: 5, right: 30, left: 20, bottom: 30}}
      >
        <XAxis
          dataKey="time"
          allowDataOverflow={true}
          type="number"
          domain={[
            endtime.clone().subtract(dataPeriod, 's').valueOf(),
            endtime.valueOf(),
          ]}
          tickFormatter={(v: number) => moment.unix(v/1000).format(datetimefmt)}
        >
          <Label value={`${xtitle} (UTC${moment().format('Z')})`} offset={5} position="bottom" />
        </XAxis>
        <YAxis
          domain={['dataMin', 'dataMax']}
          allowDataOverflow={true}
          tickCount={0}
          tickFormatter={(v: number) => v.toFixed(precision)}
        />
        <Line name={memoizedID} type="monotone" dataKey="value" dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  )
  
}

export default TracePlot;
